import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { ErrorService } from '../../../../shared/services/error.service';
import { PrintOrderDto } from '../../models/server-models/entities/print-order-dto';
import { PrintOrderService } from '../../services/print-order.service';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { SpActionButton, SpColumn } from '../../models/client-models/entities/sp-columns';
import { PrintOrderStatus } from '../../models/server-models/enums/print-order-status';

import { DatePipe } from '@angular/common';
import { LocalizedPipe } from '../../pipes/localized.pipe';
import { StringLocalized } from '../../models/shared/string-localized';
import { PaperColorType } from '../../models/server-models/enums/paper-color-type';
import { PaperSideType } from '../../models/server-models/enums/paper-side-type';
import { PaperExtraDto } from '../../models/server-models/entities/paper-extra-dto';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-user-orders',
  templateUrl: './user-orders.page.html',
  styleUrls: ['./user-orders.page.scss'],
  standalone: false,
  providers: [DatePipe, LocalizedPipe, TranslatePipe],
})
export class UserOrdersPage implements OnInit {
  constructor(private datePipe: DatePipe, private localizedPipe: LocalizedPipe) {}
  private translatePipe = inject(TranslatePipe);
  router = inject(Router);
  translateService = inject(TranslateService);
  printOrderService = inject(PrintOrderService);
  errorService = inject(ErrorService);
  orders = signal<PrintOrderDto[]>([]);

  columns: SpColumn<PrintOrderDto>[] = [];

  actions: SpActionButton<PrintOrderDto>[] = [
    { label: this.translateService.instant('BUTTON.VIEW'), icon: 'eye-outline', onClick: this.onEdit.bind(this) },
  ];

  getFormatedDate(unformattedDate: Date): string {
    return this.datePipe.transform(unformattedDate, 'hh:mm:ss dd/MM/yyyy') ?? '';
  }

  isStringLocalized(value: any): value is StringLocalized {
    return value && typeof value.en === 'string';
  }

  localize(value: any) {
    return this.localizedPipe.transform(value);
  }

  translateCol(value: any) {
    return this.translatePipe.transform(value);
  }

  ngAfterViewInit() {
    this.columns = [
      {
        name: 'PRINT.OFFICE.ORDER.TABLE.VARIATION.TITLE',
        propertySelector: (orderDto) => this.localize(orderDto.printVariation.name),
      },
      {
        name: 'PRINT.OFFICE.ORDER.TABLE.SIZE.TITLE',
        propertySelector: (orderDto) => this.localize(orderDto.paperSize.name),
      },
      {
        name: 'PRINT.OFFICE.ORDER.TABLE.TYPE.TITLE',
        propertySelector: (orderDto) => this.localize(orderDto.paperType?.name),
      },
      {
        name: 'PRINT.OFFICE.ORDER.TABLE.PAPERCOLOR.TITLE',
        propertySelector: (orderDto) => this.translateCol(this.getColorTypeDesc(orderDto.paperColorType)),
      },
      {
        name: 'PRINT.OFFICE.ORDER.TABLE.PAPER.SIDE.TITLE',
        propertySelector: (orderDto) => this.translateCol(this.getSideTypeDesc(orderDto.paperSideType)),
      },
      {
        name: 'PRINT.OFFICE.ORDER.TABLE.EXTRA.TITLE',
        propertySelector: (orderDto) => this.getJoinedExtras(orderDto.paperExtras),
      },
      {
        name: 'PRINT.OFFICE.ORDER.TABLE.FILE.COUNT.TITLE',
        propertySelector: (orderDto) => (orderDto?.files?.length ?? 0).toString(),
      },
      {
        name: 'PRINT.OFFICE.ORDER.TABLE.ORDER.TIME.TITLE',
        propertySelector: (orderDto) => this.getFormatedDate(orderDto.createdAt),
      },
      {
        name: 'PRINT.OFFICE.ORDER.TABLE.ORDER.STATUS.TITLE',
        propertySelector: (orderDto) => this.translateCol(this.getStatusDesc(orderDto.status)),
      },
    ];
  }

  getColorTypeDesc(paperColorType: PaperColorType): string {
    if (paperColorType == PaperColorType.BlackWhite) {
      return 'PRINT.COLOR.BLACK';
    } else {
      return 'PRINT.COLOR.COLORED';
    }
  }

  getSideTypeDesc(paperSideType: PaperSideType): string {
    if (paperSideType == PaperSideType.DoubleSided) {
      return 'PRINT.SIDE.DOUBLE';
    } else {
      return 'PRINT.SIDE.SINGLE';
    }
  }

  getStatusDesc(orderStatus: PrintOrderStatus): string {
    if (orderStatus == PrintOrderStatus.Created) {
      return 'PRINT.ORDER.STATUS.CREATED';
    } else if (orderStatus == PrintOrderStatus.Collected) {
      return 'PRINT.ORDER.STATUS.COLLECTED';
    } else if (orderStatus == PrintOrderStatus.Completed) {
      return 'PRINT.ORDER.STATUS.COMPLETED';
    } else if (orderStatus == PrintOrderStatus.Canceled) {
      return 'PRINT.ORDER.STATUS.CANCELED';
    } else {
      return 'PRINT.ORDER.STATUS.IN_PROGRESS';
    }
  }

  newOrderQuantity = computed(() => {
    return this.orders().filter((o) => o.status == PrintOrderStatus.Created).length;
  });

  activeOrderQuantity = computed(() => {
    return this.orders().filter((o) => o.status == PrintOrderStatus.InProgress).length;
  });

  completedOrderQuantity = computed(() => {
    return this.orders().filter((o) => o.status == PrintOrderStatus.Completed).length;
  });

  getJoinedExtras(extras: PaperExtraDto[]): string {
    let result = extras.map((e) => this.localizedPipe.transform(e.name)).join(', ');
    const resultMaxSize = 10;

    if (result.length > resultMaxSize) {
      return result.substring(1, resultMaxSize).concat('...');
    }
    return result;
  }

  ngOnInit() {
    this.loadOrders();
  }

  loadOrders() {
    this.printOrderService.getOrdersForUser().subscribe({
      next: (response) => {
        this.orders.set(response);
      },
      error: (error: HttpErrorResponse) => {
        this.errorService.handleError(error);
      },
    });
  }

  onRowClick(row: any) {
    let printOrderDto = row as PrintOrderDto;

    this.router.navigate(['/orders', printOrderDto.orderId]);
  }

  onEdit(row: any) {
    this.onRowClick(row);
  }
}
